import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

/**
 * Sessions are stored per scope: the Super Admin console, and each tenant's
 * customer app, admin panel and seller portal. A login for one tenant is never
 * sent to, or shown on, another tenant, and several can be open in separate
 * tabs at the same time.
 */
export function authScope(pathname = window.location.pathname) {
  if (pathname === "/super" || pathname.startsWith("/super/")) return "super";
  const match = pathname.match(/^\/t\/([^/]+)(?:\/(admin|seller))?(?:\/|$)/);
  if (!match) return null;
  return `t:${match[1]}:${match[2] || "customer"}`;
}

const storageKey = (scope) => `las_auth:${scope}`;

export function saveAuth({ token, user }, scope = authScope()) {
  if (!scope) return;
  localStorage.setItem(storageKey(scope), JSON.stringify({ token, user }));
}

export function getAuth(scope = authScope()) {
  if (!scope) return null;
  try {
    const session = JSON.parse(localStorage.getItem(storageKey(scope)));
    return session?.token && session?.user ? session : null;
  } catch {
    return null;
  }
}

export function clearAuth(scope = authScope()) {
  if (!scope) return;
  localStorage.removeItem(storageKey(scope));
  if (localStorage.getItem(AREA_KEY) === scopePath(scope)) localStorage.removeItem(AREA_KEY);
}

// The area signed in to last ("/super", "/t/acme/admin", ...). A native app
// reopens there instead of at its start page (see NativeTenantGuard in App.js).
const AREA_KEY = "las_last_area";
const scopePath = (scope) => {
  if (scope === "super") return "/super";
  const [, slug, area] = scope.split(":");
  return `/t/${slug}${area === "customer" ? "" : `/${area}`}`;
};
export const rememberArea = (path) => localStorage.setItem(AREA_KEY, path);
export const lastArea = () => {
  const path = localStorage.getItem(AREA_KEY);
  return path && getAuth(authScope(path)) ? path : null;
};

// The single shared session used before sessions were scoped.
localStorage.removeItem("las_token");
localStorage.removeItem("las_user");

api.interceptors.request.use((config) => {
  const session = getAuth();
  if (session) config.headers.Authorization = `Bearer ${session.token}`;
  return config;
});

/** Fired on window when the server stops accepting the saved login. */
export const SESSION_ENDED = "las:session-ended";

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // The session is no longer valid (expired, or the account was deleted):
    // drop it so the screen falls back to signed-out, and tell the console
    // that is open (see useVerifiedSession).
    if (err?.response?.status === 401 && err.config?.headers?.Authorization) {
      clearAuth();
      window.dispatchEvent(new Event(SESSION_ENDED));
    }
    return Promise.reject(err);
  }
);

export function formatINR(n) {
  if (n == null) return "₹0";
  return "₹" + Number(n).toLocaleString("en-IN");
}
